'use client';

import { GOOGLE_ANALYTICS_ID } from '@/config/publicSiteConfig';
import { useLocationSearch } from '@/routing/useAppRouter';
import { MotionConfig } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import AppQueryProvider from '@/react-query/AppQueryProvider';

import { pageView } from '@/utils/gtags';
import {
  captureLeadAttribution,
  recordLeadAttributionPage,
} from '@/utils/leadAttribution';

type Props = {
  children: React.ReactNode;
};

/**
 * body 기본 스타일은 globals.css 로 옮겼다.
 * 런타임 주입 스타일 한 겹에만 의존하면 그 스타일이 적용되기 전 프레임에
 * 캔버스가 그대로 노출된다 (다크모드에서 검게 보이던 장애).
 *
 * EmotionRegistry 도 제거했다. 공개 사이트에서 emotion 을 쓰는 코드가 더 이상 없다
 * (마지막 소비자였던 react-select 를 인하우스 Select 로 교체).
 * 어드민(pages router)은 자체 _app 에서 emotion 을 계속 쓴다.
 */
export default function AppProviders({ children }: Props) {
  return (
    <AppQueryProvider>
      <MotionConfig reducedMotion="user">
        <AnalyticsPageViews />
        {children}
      </MotionConfig>
    </AppQueryProvider>
  );
}

function AnalyticsPageViews() {
  const pathname = usePathname();
  const search = useLocationSearch();

  useEffect(() => {
    captureLeadAttribution();
    recordLeadAttributionPage(pathname ?? '/');
    if (!GOOGLE_ANALYTICS_ID) return;
    pageView(
      GOOGLE_ANALYTICS_ID,
      `${pathname ?? '/'}${window.location.search}`,
    );
  }, [pathname, search]);

  return null;
}
